import React, { useEffect, useState } from "react";
import AppUtils from "@src/utils/appUtils";
import PropTypes from "prop-types";
import { useOperatorContext } from "@src/context/operatorContext";
import { BackButton, Icon, Loader } from "@src/components/atoms";
import CardContainer from "@src/components/cards/CardContainer";
import { useDCContext } from "@src/context/dcContext";
import NotificationCard from "@src/components/cards/operator/NotificationCard";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import { useAuthContext } from "@src/context/authContext";
import { NotificationCategoryAliases } from "@src/constants/constants.js";
import { trackActionActivity } from "../../utils/userActivityTracker";
import ErrorPage from "@src/components/layout/ErrorPage";

const NotificationsPage = () => {
  const { profile } = useOperatorContext();
  const dcContext = useDCContext();
  const auth = useAuthContext();
  const { user } = profile;
  const { getDynamicReplacement } = dcContext;
  const [offset, setOffset] = useState(0);
  const limit = 10;
  const [allNotifications, setAllNotifications] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const {
    data: { notifications } = {},
    isPending: notificationsPending,
    error: notificationsError,
  } = OperatorHooks.useOperatorNotifications(
    auth?.user?.username,
    auth.activeRole,
    NotificationCategoryAliases["ALL"],
    offset,
    limit,
  );

  useEffect(() => {
    if (!!notifications) {
      setAllNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const newItems = notifications.filter((n) => !existingIds.has(n.id));
        const updated = [...prev, ...newItems];

        const newTotal = updated.length;
        if (newTotal < total + limit) {
          setHasMore(false);
          setTotal(newTotal);
        } else if (newTotal === total) {
          setHasMore(false);
        } else {
          setTotal(newTotal);
        }

        return updated;
      });
    }
  }, [notifications]);

  const onLoadMore = () => {
    if (!!hasMore) {
      setOffset((prevOffset) => prevOffset + limit);
      trackActionActivity("NOTIFICATIONS_NEXT_PAGE", {
        limit,
        offset: offset + limit,
      });
    }
  };

  const mutationCallback = (response) => {
    const readNotificationId = response.data;
    // Update the notifications state to mark the read notification
    const notification = allNotifications.find(
      (n) => n.id === readNotificationId,
    );
    if (notification) {
      notification.isRead = true;
    } else {
      console.error("Notification not found:", response);
    }
  };

  const mutation = OperatorHooks.useReadNotification(mutationCallback);

  const onReadNotification = async (notificationId) => {
    mutation.mutate({ role: auth.activeRole, notificationId });
  };

  return (
    <section className="p-4">
      <div className="relative flex flex-col items-center justify-center mb-8 h-14">
        <div className="absolute self-start ml-4">
          <BackButton trackingActionKey="NOTIFICATIONS_BACK_BUTTON" />
        </div>
        <h1 className="text-2xl md:text-4xl font-extrabold text-center leading-snug capitalize m-0">
          Notifications
        </h1>
      </div>
      <div className="flex flex-col gap-4">
        {allNotifications.length === 0 && notificationsPending ? (
          <div className="min-h-120">
            <div className="w-full h-full flex items-center justify-center">
              <Loader size="2xl" />
            </div>
          </div>
        ) : !!notificationsError || notifications?.errors ? (
          <ErrorPage pageName={"notifications"} />
        ) : !!notifications &&
          notifications.length === 0 &&
          allNotifications?.length === 0 ? (
          <div
            key={"no-notif-message"}
            className="text-center text-gray-600 text-md py-4"
          >
            {getDynamicReplacement("captain__notifications__nothing", {})}
          </div>
        ) : (
          !!allNotifications &&
          allNotifications.length > 0 &&
          allNotifications
            .reduce((acc, current) => {
              if (!acc.some((n) => n.id === current.id)) {
                acc.push(current);
              }
              return acc;
            }, [])
            .map((n) => {
              return (
                <NotificationCard
                  key={n.id}
                  notification={n}
                  user={user}
                  onRead={() => onReadNotification(n.id)}
                />
              );
            })
        )}
      </div>
      {allNotifications.length > 0 && !!notificationsPending ? (
        <div className="min-h-120">
          <div className="w-full h-full flex items-center justify-center">
            <Loader size="2xl" />
          </div>
        </div>
      ) : (
        allNotifications.length > 0 &&
        !notificationsPending &&
        !!hasMore && (
          <div className="flex justify-center py-6">
            <div
              className="p-3 mx-auto cursor-pointer text-primary-500 rounded-lg  hover:underline"
              onClick={onLoadMore}
            >
              <Icon name="refresh" backgroundColor={"transparent"} size="3xl" />
              <div className="text-2xl text-primary-500 font-bold text-center">
                {getDynamicReplacement("captain__notifications__more")}
              </div>
            </div>
          </div>
        )
      )}
    </section>
  );
};
export const notificationsPageControls = {};

NotificationsPage.propTypes = AppUtils.getPropTypeFromControls(
  notificationsPageControls,
);

export default NotificationsPage;
